import React, { useState, useMemo } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import moment from 'moment';

/**
 * AnfrageModal ermöglicht es einem Schüler, eine Terminanfrage für einen
 * bestimmten Timeslot bei einem Tutor zu stellen.
 */
export default function AnfrageModal({ show, onHide, event, tutor, onSubmit }) {
  const [skillId, setSkillId] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filtern nach Qualifikationen, die eine ID besitzen und nicht archiviert sind
  const bookableSkills = useMemo(() => {
    return tutor?.skills?.filter(skill => skill.id && skill.name && !skill.name.includes('-deleted')) || [];
  }, [tutor]);

  // Berechnen von lesbarem Datum, Uhrzeit und der Termindauer in Minuten
  const { dateStr, timeStr, duration } = useMemo(() => {
    if (!event) return {};
    return {
      dateStr: moment(event.start).format('DD.MM.YYYY'),
      timeStr: `${moment(event.start).format('HH:mm')} - ${moment(event.end).format('HH:mm')} Uhr`,
      duration: moment(event.end).diff(moment(event.start), 'minutes'),
    };
  }, [event]);

  /**
   * Sendet die Terminanfrage an das Backend.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!skillId) {
      setError('Bitte wählen Sie eine Qualifikation aus.');
      return;
    }
    
    setLoading(true);
    setError('');

    const requestData = {
      tutorId: tutor.id,
      skillId: parseInt(skillId, 10),
      // Sends timezone-aware UTC ISO-8601 string (e.g., 2026-05-19T15:00:00.000Z)
      datumZeit: moment(event.start).toISOString(),
      dauerMinuten: duration,
      beschreibung: beschreibung,
    };

    try {
      await onSubmit(requestData);
      onHide();
    } catch (err) {
      setError(err.message || 'Ein unerwarteter Fehler ist aufgetreten.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Zurücksetzen aller States nach dem Schließen des Modals
   */
  const handleExited = () => {
    setSkillId('');
    setBeschreibung('');
    setError('');
    setLoading(false);
  };

  return (
    <Modal show={show} onHide={onHide} onExited={handleExited} centered>
      <Modal.Header closeButton>
        <Modal.Title>Terminanfrage senden</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <p>
          Sie sind dabei, einen Termin bei <strong>{tutor?.vollerName}</strong> anzufragen.
        </p>
        <p>
          <strong>Datum:</strong> {dateStr}
          <br />
          <strong>Uhrzeit:</strong> {timeStr} ({duration} Minuten)
        </p>

        <Form id="anfrage-form" onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="formSkill">
            <Form.Label>Wofür benötigen Sie Hilfe?</Form.Label>
            <Form.Select 
              value={skillId} 
              onChange={(e) => setSkillId(e.target.value)}
              required
            >
              <option value="">Bitte auswählen...</option>
              {bookableSkills.map(skill => (
                <option key={skill.id} value={skill.id}>
                  {skill.name} (Niveau: {skill.niveau})
                </option>
              ))}
            </Form.Select>
            {bookableSkills.length === 0 && (
              <Form.Text className="text-muted">
                Für diesen Dozenten sind aktuell keine buchbaren Qualifikationen hinterlegt.
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3" controlId="formBeschreibung">
            <Form.Label>Zusätzliche Nachricht (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Beschreiben Sie kurz Ihr Anliegen oder Ihre Fragen..."
              value={beschreibung}
              onChange={(e) => setBeschreibung(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Abbrechen
        </Button>
        <Button 
          style={{ backgroundColor: '#b95c50', borderColor: '#b95c50' }}
          type="submit" 
          form="anfrage-form" 
          disabled={loading || bookableSkills.length === 0}
        >
          {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Anfrage senden'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
